import { DestroyRef, inject, Injectable } from "@angular/core";

import { ActivatedRoute, Router, UrlTree } from "@angular/router";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { AuthRole } from "../../../../models/auth.dto";

import { AuthService } from ".";

@Injectable({
    providedIn: "root",
})
export class AuthRedirectService {
    createNoAccessUrlTree(): import("@angular/router").MaybeAsync<import("@angular/router").GuardResult> {
        throw new Error("Method not implemented.");
    }

    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly authService = inject(AuthService);
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        this.authService.sessionExpired$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((role) => {
            this.redirectAfterSessionExpiration(role);
        });
    }

    public createLoginUrlTree(returnUrl?: string): UrlTree {
        return this.router.createUrlTree([this.getLoginRoute("user")], {
            queryParams: returnUrl ? { returnUrl } : undefined,
        });
    }

    public createAdminLoginUrlTree(returnUrl?: string): UrlTree {
        return this.router.createUrlTree([this.getLoginRoute("admin")], {
            queryParams: returnUrl ? { returnUrl } : undefined,
        });
    }

    public goToLogin(returnUrl?: string): Promise<boolean> {
        return this.router.navigateByUrl(this.createLoginUrlTree(returnUrl));
    }

    public goToAdminLogin(returnUrl?: string): Promise<boolean> {
        return this.router.navigateByUrl(this.createAdminLoginUrlTree(returnUrl));
    }

    public goToHome(): Promise<boolean> {
        return this.router.navigate(["/"]);
    }

    public redirectAfterLogin(): void {
        const returnUrl = this.activatedRoute.snapshot.queryParamMap.get("returnUrl");

        const targetUrl = this.getSafeReturnUrl(returnUrl);

        void this.router.navigateByUrl(targetUrl, { replaceUrl: true });
    }

    private redirectAfterSessionExpiration(role: AuthRole): void {
        const currentUrl = this.router.url;

        if (role === "user") {
            if (currentUrl.startsWith("/auth/login")) {
                return;
            }

            void this.goToLogin(currentUrl);
            return;
        }

        if (currentUrl.startsWith("/auth/admin/login")) {
            return;
        }

        void this.goToAdminLogin(currentUrl);
    }

    private getLoginRoute(role: AuthRole): string {
        return role === "admin" ? "/admin/auth/login" : "/auth/login";
    }

    private getSafeReturnUrl(returnUrl: string | null): string {
        if (!returnUrl) {
            return "/";
        }

        if (!returnUrl.startsWith("/") || returnUrl.startsWith("//")) {
            return "/";
        }

        return returnUrl;
    }
}
